import { Err, isOk, Result } from "@pacote/result"
import useSWR from "swr"
import useSWRMutation from "swr/mutation"
import { z } from "zod"

import {
  ApiFailure,
  BaseApiErrorResponse,
  baseApiResponseSchema,
  UnhandledApiError,
} from "./api-config"
import { withAuthClientFetcher } from "./base-fetchers"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/
type GenerateOtpPayload = {
  contacts: Partial<Record<"phonePrefix" | "phoneNumber" | "email", string>>
  name: string
  surname: string
}

const generateOtpResponseSchema = baseApiResponseSchema.extend({
  counterRefresh: z.number(),
  jwt: z.string(),
})

type GenerateOtpResponse = z.infer<typeof generateOtpResponseSchema>

type TgenerateOtpErrorResponse = {
  type: "SKEBBY" | "ALREADY_SENT" | "GENERIC_CODE_ERROR" | "CODE_ERROR"
  counterRefresh?: number
  errorMessage?: string
}

type GenerateOtpServiceResponse = Result<
  GenerateOtpResponse,
  TgenerateOtpErrorResponse
>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/
const generateOtpServiceEndpoint = "/otp-security-ms/ms/contact/generate"
/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/
const generateOtpFetcher = async (
  _key: string,
  {
    arg: {
      contacts: { email, phoneNumber, phonePrefix },
      name,
      surname,
    },
  }: { arg: GenerateOtpPayload },
): Promise<GenerateOtpServiceResponse> => {
  const responseResult = await withAuthClientFetcher<
    GenerateOtpResponse,
    BaseApiErrorResponse
  >({
    url: generateOtpServiceEndpoint,
    method: "POST",
    body: {
      name,
      surname,
      ...(phonePrefix ? { phonePrefix } : {}),
      ...(phoneNumber ? { phoneNumber } : {}),
      ...(email ? { email } : {}),
    },
    successSchemaValidator: generateOtpResponseSchema,
  })

  if (isOk(responseResult)) return responseResult

  const response = responseResult.value

  if (response.status !== 422)
    throw new UnhandledApiError({
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `A non 422 status was returned from ${generateOtpServiceEndpoint} service.`,
      originalStatus: response.status,
      data: response.data,
    })

  const errorInfos = response.data.error?.errorInfos

  const nameError =
    errorInfos && (Object.keys(errorInfos || {})[0] as keyof typeof errorInfos)

  if (!errorInfos || !nameError || !errorInfos[nameError]?.length)
    throw new UnhandledApiError({
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `Invalid errorInfos response`,
      originalStatus: response.status,
      data: errorInfos,
    })

  if (nameError === "skebby_validation")
    return Err({
      type: "SKEBBY",
    })

  const errorMessage = errorInfos[nameError][0] ?? ""

  if (errorMessage === "err.otp.alreadySent")
    return Err({ type: "ALREADY_SENT" })

  if (!["err.otp.block", "err.out.limit"].includes(errorMessage))
    return Err({
      type: "GENERIC_CODE_ERROR",
      errorMessage,
    })

  const counterRefresh =
    errorMessage === "err.otp.block"
      ? {
          counterRefresh: 4,
        }
      : {}

  return Err({
    type: "CODE_ERROR",
    ...counterRefresh,
    errorMessage:
      errorMessage === "err.otp.block"
        ? "Otp.expiredOtpCode"
        : "Otp.tooManyAttempts",
  })
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/
const useGenerateOtpMutation = (key: string) => {
  const { data } = useSWR<GenerateOtpServiceResponse>(key)

  const { trigger, reset, isMutating } = useSWRMutation(
    key,
    generateOtpFetcher,
    {
      populateCache: true,
    },
  )

  return {
    trigger,
    reset,
    data,
    isMutating,
  }
}

export { generateOtpFetcher, useGenerateOtpMutation }
export type { GenerateOtpPayload, GenerateOtpServiceResponse }
